// Generic engine for floaty DOM overlays anchored on a world entity:
// XP gains, gold gains, damage numbers, chat bubbles. They all share the
// same plumbing — RAF loop, fade-near-end-of-life, follow the entity's
// screen position, rise progressively. Specifics (text, colour, exact
// offsets) are passed in as config.
//
// Saves ~140 lines vs four near-identical files, and (importantly) any
// future fix to "how anchored overlays follow their entity" lands once
// and fixes all four at the same time. The entityScreenPos scale bug
// hit all four before this refactor because each had its own copy of
// the maths.

import type { WorldView } from "../render/world-view.js";

export interface AnchoredOverlayOptions<T> {
  layer: HTMLElement;
  worldView: WorldView;
  // Lifetime — the overlay element is removed at durationMs.
  durationMs: number;
  // The .fading class lands this many ms before durationMs so CSS can
  // transition opacity to 0 in sync with the removal.
  fadeBeforeMs: number;
  // Constant px offset added on top of the rise — negative = above the
  // anchor. Use to stack siblings (gold sits below xp, etc.).
  yOffset: number;
  // Total px the overlay rises across its lifetime (0 = stationary,
  // used by chat bubbles which position themselves entirely via CSS).
  riseProgressPx: number;
  // Build the DOM node for a fresh spawn. Returning null silently
  // drops the spawn — handy for amount=0 cases.
  createNode: (data: T) => HTMLElement | null;
}

interface ActiveEntry {
  el: HTMLElement;
  anchorId: string;
  startedAt: number;
}

export class AnchoredOverlay<T> {
  private readonly active: ActiveEntry[] = [];
  private rafId = 0;

  constructor(private readonly opts: AnchoredOverlayOptions<T>) {}

  spawn(anchorId: string, data: T, now: number = performance.now()): void {
    const el = this.opts.createNode(data);
    if (!el) return;
    // Every overlay this engine manages picks up the shared base class
    // (position: absolute, pointer-events: none, .fading transition).
    // Per-floater CSS layers font/colour/animation on top.
    el.classList.add("world-floater");
    this.opts.layer.append(el);
    this.active.push({ el, anchorId, startedAt: now });
    if (this.rafId === 0) this.tick();
  }

  destroy(): void {
    if (this.rafId) cancelAnimationFrame(this.rafId);
    this.rafId = 0;
    for (const a of this.active) a.el.remove();
    this.active.length = 0;
  }

  private tick = (): void => {
    if (this.active.length === 0) {
      this.rafId = 0;
      return;
    }
    this.rafId = requestAnimationFrame(this.tick);
    const now = performance.now();
    const { durationMs, fadeBeforeMs, yOffset, riseProgressPx, worldView } = this.opts;
    for (let i = this.active.length - 1; i >= 0; i--) {
      const a = this.active[i]!;
      const elapsed = now - a.startedAt;
      if (elapsed >= durationMs) {
        a.el.remove();
        this.active.splice(i, 1);
        continue;
      }
      if (elapsed > durationMs - fadeBeforeMs) a.el.classList.add("fading");
      const pos = worldView.entityScreenPos(a.anchorId);
      if (pos) {
        const progress = Math.min(1, elapsed / durationMs);
        const lift = -riseProgressPx * progress + yOffset;
        a.el.style.left = `${pos.sx}px`;
        a.el.style.top = `${pos.sy + lift}px`;
      }
    }
  };
}
